import { useEffect, useRef } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  dir?: "ltr" | "rtl";
  placeholder?: string;
  minHeight?: number;
}

/** Lightweight contentEditable rich-text editor (se) — bold/italic/underline,
 *  headings, lists, links, clear formatting. Faithfully reconstructed. */
export function RichTextEditor({ value, onChange, dir = "ltr", placeholder, minHeight = 260 }: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
  }, [value]);

  function exec(command: string, arg?: string) {
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  }
  function onInput() {
    if (ref.current) onChange(ref.current.innerHTML);
  }
  function insertLink() {
    const url = prompt(dir === "rtl" ? "أدخل الرابط (URL):" : "Enter URL:");
    if (url) exec("createLink", url);
  }
  function onPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }

  const btn = (label: React.ReactNode, title: string, action: () => void) => (
    <button type="button" title={title} onMouseDown={(e) => { e.preventDefault(); action(); }}>{label}</button>
  );

  return (
    <div className="kd-rte" dir={dir}>
      <div className="kd-rte-toolbar">
        {btn(<b>B</b>, "Bold", () => exec("bold"))}
        {btn(<i>I</i>, "Italic", () => exec("italic"))}
        {btn(<u>U</u>, "Underline", () => exec("underline"))}
        <span className="kd-rte-sep" />
        {btn("H", "Heading", () => exec("formatBlock", "<h3>"))}
        {btn("P", "Paragraph", () => exec("formatBlock", "<p>"))}
        <span className="kd-rte-sep" />
        {btn("• ≡", "Bullet list", () => exec("insertUnorderedList"))}
        {btn("1. ≡", "Numbered list", () => exec("insertOrderedList"))}
        <span className="kd-rte-sep" />
        {btn("🔗", "Insert link", insertLink)}
        {btn("✕", "Clear formatting", () => exec("removeFormat"))}
      </div>
      <div
        ref={ref}
        className="kd-rte-content"
        contentEditable
        onInput={onInput}
        onPaste={onPaste}
        suppressContentEditableWarning
        data-placeholder={placeholder || ""}
        style={{ minHeight }}
      />
    </div>
  );
}
